import { useCallback, useMemo } from "react";
import { useParams, useHistory } from "react-router-dom";
import {
  Loader,
  PageNotFound,
  useIcon,
  showErrorDialog,
} from "@SeemplicityDev/shared";
import {
  integrationTypeToPageLinkMap,
  ticketEndpointTypeEnum,
} from "@SeemplicityDev/ticket-master";
import FormWizard, {
  formVariantsEnum,
} from "../../../Forms/FormWizard/FormWizard";
import FindingsStep from "./FindingsStep/FindingsStep";
import { AutomationTypeEnum } from "../../AutomationsUtils";
import useAutomationSubmit from "../useAutomationSubmit/useAutomationSubmit";
import useViewsWizardData from "./useViewsWizardData";
import useAutomationWizardName from "../useAutomationWizardName/useAutomationWizardName";
import {
  VIEWS_WIZARD_NAME,
  buildViewsParams,
  getViewsSchedule,
} from "./viewsWizardUtils";

const ViewsWizard: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const history = useHistory();
  const { getSvg } = useIcon();

  const { wizardData, internalProviderId, notFound, providersLoading, error } =
    useViewsWizardData(id);

  const wizardName = useAutomationWizardName(
    VIEWS_WIZARD_NAME,
    wizardData && "name" in wizardData ? wizardData.name : undefined
  );

  const {
    onSubmit: onSubmitAutomation,
    createAutomationLoading,
    editAutomationLoading,
  } = useAutomationSubmit({
    automationType: AutomationTypeEnum.VIEW,
    automationId: id,
  });

  const steps = useMemo(
    () => [{ name: "Findings", id: "findings", form: <FindingsStep /> }],
    []
  );

  const handleWizardSubmit = useCallback(
    (data: Record<string, any>) => {
      if (!internalProviderId) {
        showErrorDialog({
          message: "No internal system is available for this account.",
        });
        return;
      }

      onSubmitAutomation(
        {
          filter: data.filter,
          scope: data.scope,
          name: data.name,
          schedule: getViewsSchedule(),
          params: buildViewsParams(internalProviderId),
        },
        () => {
          history.push(
            `/remediation/monitoring/${integrationTypeToPageLinkMap[ticketEndpointTypeEnum.INTERNAL]}`
          );
        }
      );
    },
    [onSubmitAutomation, internalProviderId, history]
  );

  return providersLoading ? (
    <Loader
      center
      img={getSvg("icon-spinner")}
      fullHeight
      iconSize={"xxxxl"}
      data-testid="views-wizard-loader"
    />
  ) : notFound || error ? (
    <PageNotFound
      subtitle={"Oops! Something went wrong..."}
      description={"For help, contact Customer Services"}
      showReachOutButton={false}
    />
  ) : (
    <FormWizard
      type={formVariantsEnum.PAGE}
      originName={id ? "Edit Automation" : "Add Automation"}
      wizardName={wizardName}
      withSummary={true}
      steps={steps}
      onCancel={() => {
        history.goBack();
      }}
      onSubmit={handleWizardSubmit}
      isSubmitting={id ? editAutomationLoading : createAutomationLoading}
      dangerSubmit={false}
      isValidated={!!id}
      submitLabel={`${id ? "Save" : "Create"}`}
      wizardData={wizardData}
      originLink={undefined}
      className={""}
    />
  );
};

export default ViewsWizard;
